// apps/web/src/features/clients/components/LegalEntityCard.tsx
//
// One legal entity: identity (form + INN) on top, then its 1..n bank sets.
//
// LEGACY COMPATIBILITY (#2242 FR-2): `requisitesSets` is optional on the wire
// so a new bundle still parses an old backend's response. When it is absent the
// card falls back to the flat `requisites` string, read-only — there is no set
// id to address a mutation to, and inventing one would write to the wrong row.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientLegalEntity, LegalEntityRequisites } from '@magimp/shared';
import { useArchiveClientLegalEntity, useRestoreClientLegalEntity } from '@magimp/shared-hooks';
import { Banner, Button, Chip, ConfirmModal, StatusBadge } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { useTouchActionStyle } from '../useMobileFieldStyle.js';
import { CopyTextButton } from './CopyTextButton.js';
import { ExpandableText } from './ExpandableText.js';
import { RequisitesSetCard } from './RequisitesSetCard.js';

export interface LegalEntityCardProps {
  clientId: string;
  entity: ClientLegalEntity;
  /** False for read-only actors and for an archived parent client. */
  canManage: boolean;
  /** True while the CLIENT's own lifecycle write is in flight (AC-10). */
  busy?: boolean;
  onEditEntity: (entity: ClientLegalEntity) => void;
  onAddSet: (entity: ClientLegalEntity) => void;
  onEditSet: (entity: ClientLegalEntity, set: LegalEntityRequisites) => void;
}

export function LegalEntityCard({
  clientId,
  entity,
  canManage,
  busy = false,
  onEditEntity,
  onAddSet,
  onEditSet,
}: LegalEntityCardProps) {
  const { t } = useTranslation(['clients', 'common']);
  const show = useToastStore((s) => s.show);
  const actionStyle = useTouchActionStyle();
  const archive = useArchiveClientLegalEntity(clientId, entity.id);
  const restore = useRestoreClientLegalEntity(clientId, entity.id);
  const [confirm, setConfirm] = useState<null | 'archive'>(null);
  const [showArchivedSets, setShowArchivedSets] = useState(false);

  const isArchived = !!entity.archivedAt;
  // This entity's own write, or the client's: either one is about to change what
  // its bank sets may legally do, so neither may be acted on meanwhile.
  const pending = archive.isPending || restore.isPending || busy;
  // An archived entity is read-only until restored: the backend refuses child
  // writes with `DOMAIN_CONFLICT { kind: 'legal-entity-archived' }`.
  const canEditChildren = canManage && !isArchived;

  const sets = entity.requisitesSets;
  const activeSets = (sets ?? []).filter((s) => !s.archivedAt);
  const archivedSets = (sets ?? []).filter((s) => !!s.archivedAt);

  const onArchive = () => {
    archive.mutate(undefined, {
      onSuccess: () => show({ tone: 'success', text: t('clients:toasts.legalArchived') }),
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };
  const onRestore = () => {
    restore.mutate(undefined, {
      onSuccess: () => show({ tone: 'success', text: t('clients:toasts.legalRestored') }),
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <li
      data-testid="client-legal-entity"
      data-legal-entity-id={entity.id}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-3)',
        padding: 'var(--s-3) var(--s-4)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: isArchived ? 'var(--bg-2)' : 'var(--bg-1)',
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-3)',
          flexWrap: 'wrap',
          minWidth: 0,
        }}
      >
        <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{entity.name}</span>
        <Chip icon="building-2">{t(`clients:legalType.${entity.type}`)}</Chip>
        {isArchived && <StatusBadge status="archived" size="sm" />}
        <span style={{ flex: 1 }} />
        {canManage && !isArchived && (
          <Button
            size="sm"
            kind="ghost"
            icon="pencil-line"
            style={actionStyle}
            disabled={pending}
            onClick={() => onEditEntity(entity)}
          >
            {t('common:actions.edit')}
          </Button>
        )}
        {canManage && !isArchived && (
          <Button
            size="sm"
            kind="ghost"
            icon="archive"
            style={actionStyle}
            disabled={pending}
            onClick={() => setConfirm('archive')}
            data-testid="client-legal-archive"
          >
            {t('common:actions.archive')}
          </Button>
        )}
        {canManage && isArchived && (
          <Button
            size="sm"
            kind="ghost"
            icon="archive-restore"
            style={actionStyle}
            disabled={pending}
            onClick={onRestore}
            data-testid="client-legal-restore"
          >
            {t('common:actions.restore')}
          </Button>
        )}
      </div>

      {/* The requisites controls below are absent, not disabled, while the
          entity is archived — the card has to say what brings them back. */}
      {canManage && isArchived && (
        <div data-testid="client-legal-archived-note">
          <Banner tone="info">{t('clients:blocked.archivedLegal')}</Banner>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--fz-12)', color: 'var(--fg-3)' }}>
          {t('clients:fields.inn')}
        </span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{entity.inn}</span>
        <CopyTextButton
          text={entity.inn}
          label={t('clients:actions.copyInnOf', { name: entity.name })}
          testId="client-copy-inn"
        />
      </div>

      {sets === undefined ? (
        // Legacy backend: flat requisites only, no addressable set.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
          <ExpandableText
            text={entity.requisites}
            expandLabel={t('clients:actions.expandRequisitesOf', { name: entity.name })}
          />
          <div>
            <CopyTextButton
              text={entity.requisites}
              label={t('clients:actions.copyRequisitesOf', {
                name: entity.name,
                entity: entity.name,
              })}
            >
              {t('clients:actions.copyRequisites')}
            </CopyTextButton>
          </div>
        </div>
      ) : (
        <>
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--s-2)',
            }}
          >
            {activeSets.map((set, i) => (
              <RequisitesSetCard
                key={set.id}
                clientId={clientId}
                legalEntityId={entity.id}
                entityName={entity.name}
                set={set}
                ordinal={i + 1}
                canManage={canEditChildren}
                busy={pending}
                isLastActive={activeSets.length === 1}
                onEdit={(s) => onEditSet(entity, s)}
              />
            ))}
          </ul>
          <div
            style={{ display: 'flex', gap: 'var(--s-3)', flexWrap: 'wrap', alignItems: 'center' }}
          >
            {canEditChildren && (
              <Button
                size="sm"
                kind="secondary"
                icon="plus"
                style={actionStyle}
                disabled={pending}
                onClick={() => onAddSet(entity)}
              >
                {t('clients:actions.addRequisites')}
              </Button>
            )}
            {archivedSets.length > 0 && (
              <Button
                size="sm"
                kind="quiet"
                style={actionStyle}
                aria-expanded={showArchivedSets}
                onClick={() => setShowArchivedSets((v) => !v)}
                data-testid="client-requisites-archive-toggle"
              >
                {showArchivedSets
                  ? t('clients:actions.hideArchive')
                  : t('clients:actions.showArchive', { count: archivedSets.length })}
              </Button>
            )}
          </div>
          {showArchivedSets && archivedSets.length > 0 && (
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--s-2)',
              }}
            >
              {archivedSets.map((set, i) => (
                <RequisitesSetCard
                  key={set.id}
                  clientId={clientId}
                  legalEntityId={entity.id}
                  entityName={entity.name}
                  set={set}
                  ordinal={activeSets.length + i + 1}
                  canManage={canEditChildren}
                  busy={pending}
                  isLastActive={false}
                  onEdit={(s) => onEditSet(entity, s)}
                />
              ))}
            </ul>
          )}
        </>
      )}

      {confirm === 'archive' && (
        <ConfirmModal
          danger
          narrowFullWidth
          title={t('clients:confirm.archiveLegalTitle')}
          text={t('clients:confirm.archiveLegalMessage', { name: entity.name })}
          confirmLabel={t('common:actions.archive')}
          onConfirm={onArchive}
          onClose={() => setConfirm(null)}
        />
      )}
    </li>
  );
}
